import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtINR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Search, Trash2, Receipt, UserPlus, User, Shield, PlusCircle, Printer, List, FilePlus2 } from "lucide-react";
import KiviHealthBridge from "@/components/KiviHealthBridge";
import { AdvisedTestsQueue, useAdvisedTests } from "@/components/AdvisedTestsQueue";
import { Stethoscope, X } from "lucide-react";

const GROUP_ORDER = ["OPD Consultation", "Emergency", "Lab", "X-Ray", "Sonography", "CT Scan", "MRI", "ECG", "Echo", "EMG",
  "Physio", "Plaster", "Stitch", "Dressing", "Day Care", "Injection", "Nebulisation", "Procedure", "IPD Charges", "Pharmacy", "Other"];
import { useAuth } from "@/context/AuthContext";

const NUMERIC = new Set(["age", "mobile", "pincode"]);
const MAX = { age: 3, mobile: 10, pincode: 6 };

/**
 * Walk-in Investigation — for patients who come only for X-Ray / ECG / Stitch
 * Removal etc. and don't need a doctor consultation.  Captures identity,
 * picks investigations from the master, allows per-line rate override for
 * discounts, and creates an OPD record (is_walkin=true) so the same billing
 * & 58 mm print pipeline can be reused.
 */
export default function WalkinInvestigation() {
  const nav = useNavigate();
  const { user } = useAuth();
  const canDelete = ["admin", "super_admin"].includes(user?.role || "");
  const [tab, setTab] = useState("new");           // "new" | "recent" | "advised"
  const advisedQ = useAdvisedTests();
  const [advised, setAdvised] = useState(null);     // queue row being billed

  // Recent list state
  const [recent, setRecent] = useState([]);
  const [rQuery, setRQuery] = useState("");
  const [rFrom, setRFrom] = useState("");
  const [rTo, setRTo] = useState("");
  const [rLoading, setRLoading] = useState(false);
  const loadRecent = useCallback(async () => {
    setRLoading(true);
    try {
      const params = {};
      if (rQuery.trim()) params.search = rQuery.trim();
      if (rFrom) params.from_date = rFrom;
      if (rTo) params.to_date = rTo;
      const r = await api.get("/walkin-investigations", { params });
      setRecent(r.data || []);
    } catch { toast.error("Failed to load recent walk-ins"); }
    finally { setRLoading(false); }
  }, [rQuery, rFrom, rTo]);
  useEffect(() => { if (tab === "recent") loadRecent(); }, [tab, loadRecent]);
  const reprint = (row) => window.open(`/bill/opd/${row.id}/print?layout=thermal58`, "_blank");
  const reprintA4 = (row) => window.open(`/bill/opd/${row.id}/print?layout=a4`, "_blank");
  const deleteWalkin = async (row) => {
    if (!window.confirm(`Delete walk-in ${row.opd_no} — ${row.patient_name}?\n\nThis also removes the bill items, payments and any central investigation orders. The patient master record is kept.`)) return;
    try {
      await api.delete(`/walkin-investigations/${row.id}`);
      toast.success(`${row.opd_no} deleted`);
      loadRecent();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const [master, setMaster] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState([]); // [{name, quantity, rate, body_part, is_custom?}]
  const [saving, setSaving] = useState(false);
  const [custom, setCustom] = useState({ name: "", rate: "" });

  // Payment tracking — captured before saving
  const [payment, setPayment] = useState({
    mode: "CASH",              // CASH | UPI | PMJAY | PACKAGE | CARD
    paid_amount: "",           // blank => full total; auto-zero for scheme
    reference_no: "",          // UPI ref / PMJAY case no / package id
    scheme_name: "",           // e.g. PMJAY / MA-Yojana / Corporate Package name
  });

  // Patient section — either pick an existing UHID or fill fresh details
  const [existingPatients, setExistingPatients] = useState([]);
  const [pickedPatient, setPickedPatient] = useState(null);
  const [pQ, setPQ] = useState("");
  const [kiviOpen, setKiviOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", age: "", gender: "Male", mobile: "", address: "",
    city: "Jamnagar", state: "Gujarat", pincode: "", reference_doctor: "", notes: "",
  });

  useEffect(() => {
    api.get("/investigation-master", { params: { active: "true" } }).then((r) => setMaster(r.data || []));
  }, []);

  useEffect(() => {
    const t = pQ.trim();
    if (t.length < 2) { setExistingPatients([]); return; }
    const timer = setTimeout(() => {
      api.get("/patients", { params: { q: t, limit: 8 } }).then((r) => setExistingPatients(r.data || []));
    }, 250);
    return () => clearTimeout(timer);
  }, [pQ]);

  const [grp, setGrp] = useState("All");
  const groups = useMemo(() => {
    const order = [];
    master.forEach((m) => { const g = m.service_group || "Other"; if (!order.includes(g)) order.push(g); });
    return order.sort((a, b) => GROUP_ORDER.indexOf(a) - GROUP_ORDER.indexOf(b));
  }, [master]);
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return master.filter((m) => (grp === "All" || (m.service_group || "Other") === grp)
      && (!t || (m.name || "").toLowerCase().includes(t) || (m.code || "").toLowerCase().includes(t)));
  }, [master, q, grp]);
  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach((m) => { (map[m.service_group || "Other"] ||= []).push(m); });
    return groups.filter((g) => map[g]).map((g) => [g, map[g]]);
  }, [filtered, groups]);

  const addInvestigation = (m) => {
    if (selected.some((s) => s.name === m.name)) {
      toast("Already added");
      return;
    }
    setSelected([...selected, { name: m.name, quantity: 1, rate: Number(m.rate || 0), body_part: "" }]);
  };
  const addCustom = () => {
    const nm = (custom.name || "").trim();
    const rt = parseFloat(custom.rate);
    if (!nm) return toast.error("Enter investigation name");
    if (Number.isNaN(rt) || rt < 0) return toast.error("Enter a valid amount");
    if (selected.some((s) => s.name.toLowerCase() === nm.toLowerCase())) {
      return toast.error("Already added");
    }
    setSelected([...selected, { name: nm, quantity: 1, rate: rt, body_part: "", is_custom: true }]);
    setCustom({ name: "", rate: "" });
    toast.success("Custom investigation added");
  };
  const updateLine = (i, patch) => setSelected(selected.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  const removeLine = (i) => setSelected(selected.filter((_, idx) => idx !== i));

  const total = selected.reduce((s, it) => s + (Number(it.rate) || 0) * (Number(it.quantity) || 1), 0);

  const pickExisting = (p) => {
    setPickedPatient(p);
    setForm({ ...form, name: p.name, age: String(p.age || ""), gender: p.gender || "Male",
              mobile: p.mobile || "", address: p.address || "", city: p.city || "",
              state: p.state || "", pincode: p.pincode || "" });
    setExistingPatients([]);
    setPQ("");
  };
  const clearPicked = () => { setPickedPatient(null); setAdvised(null); setForm({ ...form, name: "", age: "", mobile: "", address: "", city: "Jamnagar", state: "Gujarat", pincode: "" }); };

  const billAdvised = (row) => {
    const p = row.patient;
    setPickedPatient(p);
    setAdvised(row);
    setForm({ ...form, name: p.name || "", age: String(p.age || ""), gender: p.gender || "Male", mobile: p.mobile || "",
              address: p.address || "", city: p.city || "", state: p.state || "", pincode: p.pincode || "",
              reference_doctor: row.doctor_name || "", notes: "" });
    setSelected(row.tests.map((t) => ({ name: t.name, quantity: t.quantity || 1, rate: Number(t.rate || 0), body_part: t.body_part || "", views: t.views || "" })));
    setPayment({ mode: "CASH", paid_amount: "", reference_no: "", scheme_name: "" });
    setTab("new");
  };

  const submit = async () => {
    if (selected.length === 0) return toast.error("Add at least one investigation");
    if (!pickedPatient) {
      if (!form.name.trim() || !form.mobile.trim() || !form.age) return toast.error("Name, mobile and age are required");
    }
    // Build payment payload — normalise for scheme modes
    const isScheme = payment.mode === "PMJAY" || payment.mode === "PACKAGE";
    const paid = isScheme ? 0 : (payment.paid_amount === "" ? total : parseFloat(payment.paid_amount) || 0);
    let payment_status = "PAID";
    if (isScheme) payment_status = "SCHEME";
    else if (paid <= 0) payment_status = "UNPAID";
    else if (paid < total - 0.01) payment_status = "PARTIAL";
    if (isScheme && !payment.scheme_name.trim()) {
      return toast.error(`Enter ${payment.mode} scheme / package name`);
    }
    const pay_payload = {
      payment_mode: payment.mode,
      paid_amount: paid,
      payment_status,
      reference_no: payment.reference_no.trim(),
      scheme_name: payment.scheme_name.trim() || (isScheme ? payment.mode : ""),
    };
    setSaving(true);
    try {
      const payload = pickedPatient
        ? { patient_id: pickedPatient.id, investigations: selected, notes: form.notes, reference_doctor: form.reference_doctor, advised_opd_id: advised?.opd_id || null, ...pay_payload }
        : { ...form, age: parseInt(form.age), investigations: selected, ...pay_payload };
      const { data } = await api.post("/walkin-investigations", payload);
      toast.success(`Registered · ${data.opd_no}`);
      // Immediately open 58 mm slip in a new tab
      window.open(`/bill/opd/${data.opd_id}/print?layout=thermal58`, "_blank");
      // Refresh the recent tab too so it's warm the next time user opens it.
      loadRecent();
      if (advised) advisedQ.load();
      // Reset
      setSelected([]);
      clearPicked();
      setForm({ ...form, name: "", age: "", mobile: "", address: "", city: "Jamnagar", state: "Gujarat", pincode: "", reference_doctor: "", notes: "" });
      setPayment({ mode: "CASH", paid_amount: "", reference_no: "", scheme_name: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create walk-in visit");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="walkin-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-display text-2xl font-semibold">Walk-in Investigation</h1>
          <p className="text-sm text-slate-500">For patients who came only for reports · X-ray · ECG · stitch removal · dressing (no doctor consultation).</p>
        </div>
        <Badge variant="outline" className="rounded-sm">No doctor required</Badge>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-slate-200" data-testid="walkin-tabs">
        <button
          data-testid="walkin-tab-new"
          onClick={() => setTab("new")}
          className={`px-4 h-9 text-sm border-b-2 transition ${tab === "new" ? "border-blue-700 text-blue-800 font-semibold" : "border-transparent text-slate-500 hover:text-slate-700"}`}
        >
          <FilePlus2 className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5"/> New Entry
        </button>
        <button
          data-testid="walkin-tab-recent"
          onClick={() => setTab("recent")}
          className={`px-4 h-9 text-sm border-b-2 transition ${tab === "recent" ? "border-blue-700 text-blue-800 font-semibold" : "border-transparent text-slate-500 hover:text-slate-700"}`}
        >
          <List className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5"/> Recent
          {recent.length > 0 && <span className="ml-1 text-[10px] text-slate-400">({recent.length})</span>}
        </button>
        <button
          data-testid="walkin-tab-advised"
          onClick={() => setTab("advised")}
          className={`px-4 h-9 text-sm border-b-2 transition ${tab === "advised" ? "border-blue-700 text-blue-800 font-semibold" : "border-transparent text-slate-500 hover:text-slate-700"}`}
        >
          <Stethoscope className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5"/> Advised by Doctor
          {advisedQ.rows.length > 0 && <span className="ml-1 text-[10px] bg-amber-500 text-white rounded-full px-1.5" data-testid="advised-count">{advisedQ.rows.length}</span>}
        </button>
      </div>

      {tab === "advised" ? (
        <AdvisedTestsQueue q={advisedQ} onBill={billAdvised} />
      ) : tab === "recent" ? (
        <RecentPanel
          rows={recent}
          loading={rLoading}
          query={rQuery} setQuery={setRQuery}
          from={rFrom} setFrom={setRFrom}
          to={rTo} setTo={setRTo}
          canDelete={canDelete}
          onRefresh={loadRecent}
          onReprint={reprint}
          onReprintA4={reprintA4}
          onDelete={deleteWalkin}
          onNew={() => setTab("new")}
        />
      ) : (

      <>
      {advised && (
        <div className="flex items-center justify-between border border-amber-300 bg-amber-50 px-3 py-2 text-sm" data-testid="advised-banner">
          <span><Stethoscope className="w-4 h-4 inline -mt-0.5 mr-1 text-amber-700"/>Billing tests advised by <b>{advised.doctor_name}</b> · {advised.opd_no}. Remove tests the patient doesn't want (trash icon) or change rates, then save. Removed tests stay in the queue.</span>
          <button onClick={() => { setSelected([]); clearPicked(); }} className="text-amber-800 hover:text-red-700" data-testid="advised-banner-clear"><X className="w-4 h-4"/></button>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Patient block */}
        <div className="bg-white border p-4 space-y-3">
          <div className="flex items-center gap-2 label-tiny"><User className="w-3.5 h-3.5"/> Patient</div>

          {/* Existing patient search */}
          <div>
            <Label className="text-xs">Existing patient (search UHID / mobile / name)</Label>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-slate-400"/>
              <Input value={pQ} onChange={(e) => setPQ(e.target.value)} placeholder="Type at least 2 characters" className="pl-7 h-9" data-testid="walkin-patient-search" disabled={!!pickedPatient}/>
            </div>
            {pickedPatient && (
              <div className="mt-1 flex items-center justify-between border border-teal-200 bg-teal-50 rounded-sm px-2 py-1 text-xs">
                <span><b>{pickedPatient.name}</b> · <span className="font-mono">{pickedPatient.uhid}</span> · {pickedPatient.mobile}</span>
                <button onClick={clearPicked} className="text-red-700 hover:underline">Change</button>
              </div>
            )}
            {pickedPatient && (
              <div className="mt-2">
                <Label className="text-xs">Reference Doctor (optional)</Label>
                <Input value={form.reference_doctor} onChange={(e) => setForm({ ...form, reference_doctor: e.target.value })} className="mt-1 h-9" data-testid="walkin-picked-reference-doctor"/>
              </div>
            )}
            {existingPatients.length > 0 && !pickedPatient && (
              <div className="mt-1 border rounded-sm max-h-40 overflow-y-auto text-sm">
                {existingPatients.map((p) => (
                  <button key={p.id} type="button" onClick={() => pickExisting(p)} className="w-full text-left px-2 py-1 hover:bg-blue-50 border-b last:border-b-0" data-testid={`walkin-pick-${p.uhid}`}>
                    <div className="font-medium">{p.name}</div>
                    <div className="text-[10px] text-slate-500">{p.uhid} · {p.mobile} · {p.age}/{p.gender}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Or new patient */}
          {!pickedPatient && (
            <>
              <div className="flex items-center gap-1 text-[11px] text-slate-500"><UserPlus className="w-3 h-3"/> New patient — fill below</div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  ["name", "Full Name"], ["age", "Age"], ["mobile", "Mobile"], ["gender", "Gender"],
                  ["address", "Address"], ["city", "City"], ["state", "State"], ["pincode", "Pincode"],
                  ["reference_doctor", "Reference Doctor (optional)"],
                ].map(([k, l]) => (
                  <div key={k}>
                    <Label className="text-xs">{l}</Label>
                    {k === "gender" ? (
                      <select value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1 h-9 border border-slate-300 w-full px-2 text-sm" data-testid={`walkin-${k}`}>
                        <option>Male</option><option>Female</option><option>Other</option>
                      </select>
                    ) : (
                      <Input
                        className="mt-1 h-9"
                        value={form[k]}
                        onChange={(e) => {
                          let v = e.target.value;
                          if (NUMERIC.has(k)) v = v.replace(/\D/g, "").slice(0, MAX[k]);
                          setForm({ ...form, [k]: v });
                        }}
                        data-testid={`walkin-${k}`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          <div>
            <Label className="text-xs">Notes / Referral (optional)</Label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 h-9" placeholder="e.g. Post-op review, referred from Dr. X" data-testid="walkin-notes"/>
          </div>
        </div>

        {/* Investigations block */}
        <div className="bg-white border p-4 space-y-3">
          <div className="flex items-center gap-2 label-tiny"><Receipt className="w-3.5 h-3.5"/> Investigations · Procedures</div>

          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-slate-400"/>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search X-ray / ECG / USG / Lab tests" className="pl-7 h-9" data-testid="walkin-inv-search"/>
          </div>

          <div className="flex flex-wrap gap-1" data-testid="walkin-group-tabs">
            {["All", ...groups].map((g) => (
              <button key={g} type="button" onClick={() => setGrp(g)} data-testid={`walkin-group-${g}`}
                className={`px-2 py-0.5 text-[11px] border rounded-sm ${grp === g ? "border-blue-700 bg-blue-700 text-white" : "border-slate-300 bg-white hover:border-blue-400"}`}>
                {g}
              </button>
            ))}
          </div>

          <div className="border rounded-sm max-h-72 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="p-3 text-xs text-slate-500 text-center">No matches in master · use <b>Add Custom</b> below</div>
            ) : grouped.map(([g, list]) => (
              <div key={g}>
                <div className="sticky top-0 bg-slate-100 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-600">{g} · {list.length}</div>
                {list.map((m) => (
                  <button key={m.id || m.name} type="button" onClick={() => addInvestigation(m)} className="w-full flex justify-between items-center px-2 py-1.5 border-b last:border-b-0 hover:bg-blue-50 text-sm text-left" data-testid={`walkin-add-${m.name}`}>
                    <span>{m.name}</span>
                    <span className="font-mono text-xs text-slate-600">{fmtINR(m.rate)}</span>
                  </button>
                ))}
              </div>
            ))}
          </div>

          {/* Custom investigation — for tests not in master */}
          <div className="border border-dashed border-amber-300 bg-amber-50 rounded-sm p-2" data-testid="walkin-custom-block">
            <div className="text-[11px] font-semibold text-amber-800 mb-1 flex items-center gap-1">
              <PlusCircle className="w-3 h-3"/> Add Custom Investigation
              <span className="ml-auto text-[10px] font-normal text-amber-700">Use when not found in master</span>
            </div>
            <div className="flex gap-1">
              <Input
                placeholder="e.g. Special Culture Test"
                value={custom.name}
                onChange={(e) => setCustom({ ...custom, name: e.target.value })}
                onKeyDown={(e) => { if (e.key === "Enter") addCustom(); }}
                className="h-8 text-xs flex-1"
                data-testid="walkin-custom-name"
              />
              <Input
                type="number"
                min="0"
                step="1"
                placeholder="₹ Amount"
                value={custom.rate}
                onChange={(e) => setCustom({ ...custom, rate: e.target.value })}
                onKeyDown={(e) => { if (e.key === "Enter") addCustom(); }}
                className="h-8 text-xs w-24"
                data-testid="walkin-custom-rate"
              />
              <Button
                type="button"
                onClick={addCustom}
                className="h-8 bg-amber-600 hover:bg-amber-700 rounded-sm text-xs px-3"
                data-testid="walkin-custom-add"
              >
                Add
              </Button>
            </div>
          </div>

          <div className="border rounded-sm">
            <div className="bg-slate-50 px-2 py-1.5 text-[11px] font-semibold border-b flex justify-between">
              <span>{selected.length} selected</span>
              <span>Total {fmtINR(total)}</span>
            </div>
            {selected.length === 0 ? (
              <div className="p-4 text-center text-xs text-slate-500">Add investigations from the list above</div>
            ) : (
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left border-b bg-slate-50">
                    <th className="p-1.5">Investigation</th>
                    <th className="p-1.5 w-14">Qty</th>
                    <th className="p-1.5 w-20">Rate ₹</th>
                    <th className="p-1.5 w-20 text-right">Amount</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {selected.map((s, i) => (
                    <tr key={`${s.name}-${i}`} className="border-b last:border-b-0">
                      <td className="p-1.5">
                        <div className="font-medium flex items-center gap-1">
                          {s.name}
                          {s.is_custom && <Badge variant="outline" className="text-[9px] border-amber-400 text-amber-700 h-4 px-1">CUSTOM</Badge>}
                        </div>
                        <Input value={s.body_part || ""} onChange={(e) => updateLine(i, { body_part: e.target.value })} placeholder="Body part / notes (optional)" className="h-6 text-[10px] mt-0.5"/>
                      </td>
                      <td className="p-1.5"><Input type="number" min="1" value={s.quantity} onChange={(e) => updateLine(i, { quantity: parseInt(e.target.value) || 1 })} className="h-7 w-12 text-xs" data-testid={`walkin-line-qty-${i}`}/></td>
                      <td className="p-1.5"><Input type="number" min="0" value={s.rate} onChange={(e) => updateLine(i, { rate: parseFloat(e.target.value) || 0 })} className="h-7 w-20 text-xs" data-testid={`walkin-line-rate-${i}`}/></td>
                      <td className="p-1.5 text-right font-mono">{fmtINR((s.rate || 0) * (s.quantity || 1))}</td>
                      <td className="p-1.5 text-center"><button onClick={() => removeLine(i)} data-testid={`walkin-line-remove-${i}`} className="text-red-600 hover:bg-red-50 p-0.5 rounded-sm"><Trash2 className="w-3 h-3"/></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-sm px-3 py-2">
            <span className="text-xs">Grand Total (editable per line)</span>
            <span className="font-mono font-semibold text-blue-800 text-lg">{fmtINR(total)}</span>
          </div>

          {/* Payment capture — mode, amount, reference */}
          <div className="border rounded-sm p-2.5 bg-slate-50 space-y-2" data-testid="walkin-payment-block">
            <div className="label-tiny flex items-center gap-1 text-slate-700"><Receipt className="w-3 h-3"/> Payment</div>
            <div className="grid grid-cols-4 gap-2">
              {["CASH", "UPI", "CARD", "PMJAY", "PACKAGE"].map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setPayment((p) => ({ ...p, mode: m, paid_amount: (m === "PMJAY" || m === "PACKAGE") ? "0" : p.paid_amount }))}
                  className={`h-8 text-xs border rounded-sm transition ${payment.mode === m ? "bg-blue-700 text-white border-blue-700" : "bg-white text-slate-700 border-slate-300 hover:border-blue-400"}`}
                  data-testid={`walkin-pay-mode-${m}`}
                >
                  {m}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500">
                  {(payment.mode === "PMJAY" || payment.mode === "PACKAGE") ? "Paid Amount (₹) · Scheme covers total" : "Paid Amount (₹) · blank = full"}
                </Label>
                <Input
                  type="number" min="0"
                  value={payment.paid_amount}
                  placeholder={String(total)}
                  disabled={payment.mode === "PMJAY" || payment.mode === "PACKAGE"}
                  onChange={(e) => setPayment({ ...payment, paid_amount: e.target.value })}
                  className="h-9 text-sm mt-0.5"
                  data-testid="walkin-pay-amount"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500">
                  {payment.mode === "UPI" ? "UPI Ref No" : (payment.mode === "PMJAY" || payment.mode === "PACKAGE") ? "Case / Package No" : payment.mode === "CARD" ? "Card Ref No" : "Reference (optional)"}
                </Label>
                <Input
                  value={payment.reference_no}
                  onChange={(e) => setPayment({ ...payment, reference_no: e.target.value })}
                  className="h-9 text-sm mt-0.5"
                  data-testid="walkin-pay-refno"
                />
              </div>
            </div>
            {(payment.mode === "PMJAY" || payment.mode === "PACKAGE") && (
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500">
                  {payment.mode === "PMJAY" ? "Scheme (e.g. PMJAY / MA-Yojana)" : "Package Name"}
                </Label>
                <Input
                  value={payment.scheme_name}
                  onChange={(e) => setPayment({ ...payment, scheme_name: e.target.value })}
                  className="h-9 text-sm mt-0.5"
                  placeholder={payment.mode === "PMJAY" ? "PMJAY" : "Corporate Package"}
                  data-testid="walkin-pay-scheme"
                />
              </div>
            )}
          </div>

          <Button onClick={submit} disabled={saving || selected.length === 0} data-testid="walkin-submit" className="w-full bg-blue-700 hover:bg-blue-800 rounded-sm">
            {saving ? "Saving…" : "Save & Print 58mm Slip"}
          </Button>
          {(pickedPatient || (form.name && form.mobile)) && (
            <Button
              variant="outline"
              onClick={() => setKiviOpen(true)}
              data-testid="walkin-kivi"
              className="w-full rounded-sm mt-2 border-teal-400 text-teal-700 hover:bg-teal-50"
            >
              <Shield className="w-4 h-4 mr-1"/> Create ABHA via KiviHealth
            </Button>
          )}
        </div>
      </div>
      <KiviHealthBridge
        open={kiviOpen}
        onOpenChange={setKiviOpen}
        patient={pickedPatient || (form.name && form.mobile ? { ...form, age: parseInt(form.age) || 0 } : null)}
        onLinked={() => setKiviOpen(false)}
      />
      </>
      )}
    </div>
  );
}

/* ─────────────────────── RECENT LIST PANEL ─────────────────────── */
function RecentPanel({ rows, loading, query, setQuery, from, setFrom, to, setTo, canDelete, onRefresh, onReprint, onReprintA4, onDelete, onNew }) {
  return (
    <div className="bg-white border rounded-sm" data-testid="walkin-recent">
      <div className="p-3 border-b flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[220px]">
          <Label className="text-[11px] uppercase tracking-widest text-slate-500">Search</Label>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-slate-400"/>
            <Input
              placeholder="Patient · UHID · OPD No"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") onRefresh(); }}
              className="pl-7 h-9"
              data-testid="walkin-recent-search"
            />
          </div>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-slate-500">From</Label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9" data-testid="walkin-recent-from"/>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-slate-500">To</Label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9" data-testid="walkin-recent-to"/>
        </div>
        <Button variant="outline" onClick={onRefresh} className="h-9 rounded-sm" data-testid="walkin-recent-refresh">Apply</Button>
        <Button onClick={onNew} className="h-9 bg-blue-700 hover:bg-blue-800 rounded-sm">
          <FilePlus2 className="w-4 h-4 mr-1"/> New Entry
        </Button>
      </div>

      <div className="overflow-auto">
        <table className="w-full text-xs" data-testid="walkin-recent-table">
          <thead className="bg-slate-50 border-b">
            <tr className="text-left">
              <th className="p-2">Date</th>
              <th>OPD No</th>
              <th>Patient</th>
              <th>UHID</th>
              <th>Age/Sex</th>
              <th>Tests</th>
              <th>Ref. Doctor</th>
              <th>Payment</th>
              <th className="text-right">Amount</th>
              <th className="text-right pr-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={10} className="p-6 text-center text-slate-500">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={10} className="p-6 text-center text-slate-500">No walk-ins match the filters.</td></tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.id} className="border-b hover:bg-slate-50" data-testid={`walkin-row-${r.id}`}>
                <td className="p-2 text-[11px] text-slate-600">{(r.visit_date || "").slice(0, 16).replace("T", " ")}</td>
                <td className="font-mono text-[11px]">{r.opd_no}</td>
                <td className="font-medium">{r.patient_name}</td>
                <td className="font-mono text-[11px] text-slate-600">{r.uhid}</td>
                <td>{r.age ?? "-"}/{(r.gender || "-").slice(0, 1)}</td>
                <td>
                  <div className="flex flex-wrap gap-1">
                    {(r.investigations || []).slice(0, 4).map((n, i) => (
                      <Badge key={`${r.id}-${n}-${i}`} variant="outline" className="text-[10px]">{n}</Badge>
                    ))}
                    {(r.investigations || []).length > 4 && <span className="text-[10px] text-slate-400">+{r.investigations.length - 4}</span>}
                  </div>
                </td>
                <td className="text-[11px] text-slate-600">{r.reference_doctor || "—"}</td>
                <td>
                  <div className="flex flex-col gap-0.5">
                    <Badge variant="outline" className={`text-[10px] w-fit ${
                      r.payment_status === "PAID" ? "border-teal-400 text-teal-700 bg-teal-50" :
                      r.payment_status === "SCHEME" ? "border-purple-400 text-purple-700 bg-purple-50" :
                      r.payment_status === "PARTIAL" ? "border-amber-400 text-amber-700 bg-amber-50" :
                      "border-red-400 text-red-700 bg-red-50"
                    }`}>
                      {r.payment_mode || "CASH"} · {r.payment_status || "PAID"}
                    </Badge>
                    {r.imported_to_ipd_id && (
                      <span className="text-[9px] text-blue-700">→ Added to IPD bill</span>
                    )}
                  </div>
                </td>
                <td className="text-right font-mono">{fmtINR(r.total_amount)}</td>
                <td className="text-right pr-3 whitespace-nowrap">
                  <button title="Print 58mm slip" onClick={() => onReprint(r)}
                    className="inline-flex items-center gap-1 text-blue-700 hover:bg-blue-50 rounded-sm px-1.5 py-1 text-[11px]"
                    data-testid={`walkin-reprint-${r.id}`}>
                    <Printer className="w-3.5 h-3.5"/> 58mm
                  </button>
                  <button title="Print A4 bill" onClick={() => onReprintA4(r)}
                    className="inline-flex items-center gap-1 text-blue-700 hover:bg-blue-50 rounded-sm px-1.5 py-1 text-[11px] ml-0.5"
                    data-testid={`walkin-reprint-a4-${r.id}`}>
                    <Receipt className="w-3.5 h-3.5"/> A4
                  </button>
                  {canDelete && (
                    <button title="Delete walk-in" onClick={() => onDelete(r)}
                      className="inline-flex items-center gap-1 text-red-700 hover:bg-red-50 rounded-sm px-1.5 py-1 text-[11px] ml-0.5"
                      data-testid={`walkin-delete-${r.id}`}>
                      <Trash2 className="w-3.5 h-3.5"/>
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
